import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUp, BookOpen, Clock, ArrowRight } from 'lucide-react';
import '../Tailwind.css';

interface BlogPost {
  id: number;
  title: string;
  category: 'Oyster' | 'Button' | 'Commercial';
  readTime: string;
  content: string;
  image: string;
}

const BLOG_POSTS_DATA: BlogPost[] = [
  {
    id: 1,
    title: "Nutritional Powerhouse: Fresh Oyster Mushrooms",
    category: "Oyster",
    readTime: "3 min read",
    content: "Fresh oyster mushrooms are packed with essential nutrients, antioxidants, and lean proteins that actively strengthen metabolic and overall immune health.",
    image: '/images/blog1.webp'
  },
  {
    id: 2,
    title: "Why Dry Oyster Mushrooms Belong in Your Pantry",
    category: "Oyster",
    readTime: "2 min read",
    content: "An incredible commercial alternative, dried oyster mushrooms deliver an intense, concentrated umami profile with an extended structural shelf life.",
    image: '/images/blog2.webp'
  },
  {
    id: 3,
    title: "From Substrate to Harvest: The Cultivation Cycle",
    category: "Commercial",
    readTime: "4 min read",
    content: "A professional walk-through showing exactly how meticulously prepped agricultural byproducts turn into premium-grade oyster mushroom yields.",
    image: '/images/blog3.webp'
  },
  {
    id: 4,
    title: "Fresh vs. Dried: Finding Your Commercial Fit",
    category: "Commercial",
    readTime: "3 min read",
    content: "Analyzing culinary applications, market shipping profiles, and economic margins to evaluate when to sell fresh versus processing into dry variants.",
    image: '/images/blog4.webp'
  },
  {
    id: 5,
    title: "Culinary Standard: Recipes with Oyster Mushrooms",
    category: "Oyster",
    readTime: "3 min read",
    content: "Explore simple, high-impact recipes engineered to feature the delicate texture and nutty aroma profiles of perfectly harvested oyster crops.",
    image: '/images/blog5.webp'
  },
  {
    id: 6,
    title: "The Superfood Status of Modern Fungi Dieting",
    category: "Commercial",
    readTime: "4 min read",
    content: "Scientific analysis of why the global culinary demand is shifting hard toward medicinal and gourmet mushrooms as premium source proteins.",
    image: '/images/blog6.webp'
  },
  {
    id: 7,
    title: "Storage Rules for Fresh and Dehydrated Stock",
    category: "Commercial",
    readTime: "2 min read",
    content: "Industrial metrics and temperature management benchmarks to keep your harvests fresh and prevent spoilage down the cold storage chain.",
    image: '/images/blog7.webp'
  },
  {
    id: 8,
    title: "The Wardha Mushroom Quality Production Standard",
    category: "Commercial",
    readTime: "3 min read",
    content: "A detailed breakdown of our sterile spawning methods, localized premium substrate selection, and high-efficiency commercial buyback systems.",
    image: '/images/blog8.webp'
  },
  {
    id: 9,
    title: "Mushrooms in Modern Indian Culinary Ecosystems",
    category: "Commercial",
    readTime: "4 min read",
    content: "Tracing how local retail spaces, premium hotel chains, and global exports are transforming the profitability of mushroom farming in India.",
    image: '/images/blog9.webp'
  },
  {
    id: 10,
    title: "Button Mushrooms: Vitamin Boost in Daily Meals",
    category: "Button",
    readTime: "3 min read",
    content: "Button mushrooms are packed with nutrients like vitamin D, B-complex, and selenium. Low in calories and high in antioxidants, they make an ideal, health-focused addition to everyday curries, stir-fries, and premium salads.",
    image: '/images/Buttonm.webp'
  },
  {
    id: 11,
    title: "Micro-Farming Setup: Big Yields in Small Spaces",
    category: "Button",
    readTime: "4 min read",
    content: "You do not need sprawling acres to run a professional cultivation line. A controlled 10×10 ft space running a precision compost framework can yield consistent, lucrative agribusiness returns with low structural overhead.",
    image: '/images/BB.webp'
  },
  {
    id: 12,
    title: "Dehydrated Button Stocks: Commercial Kitchen Choice",
    category: "Button",
    readTime: "3 min read",
    content: "Dried button varieties provide extreme kitchen convenience, delivering concentrated rich umami depths for corporate hotel soups, instant gravies, and restaurant chains with quick warm-water rehydration.",
    image: '/images/bbt.webp'
  }
];

const BlogPosts: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [expandedPost, setExpandedPost] = useState<number | null>(null);
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = ['All', 'Oyster', 'Button', 'Commercial'];

  const filteredPosts = selectedCategory === 'All' 
    ? BLOG_POSTS_DATA 
    : BLOG_POSTS_DATA.filter(post => post.category === selectedCategory);

  return (
    <div className="bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-gray-100 min-h-screen py-16 px-4 sm:px-6 lg:px-8 font-sans antialiased">
      <div className="max-w-7xl mx-auto">
        
        {/* Modern Header Section */}
        <header className="text-center mb-16 relative">
          <div className="absolute inset-x-0 top-0 h-40 bg-green-500/5 rounded-full blur-3xl pointer-events-none"></div>
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-xs font-bold uppercase tracking-widest text-green-500 bg-green-500/10 px-3 py-1 rounded-full border border-green-500/20">
              Knowledge Hub
            </span>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mt-4 mb-4 text-white">
              Cultivation & Market Insights
            </h1>
            <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto font-medium">
              Actionable agronomy logs, functional nutrition breakdowns, and structural setups for commercial mushroom producers.
            </p>
          </motion.div>
        </header>

        {/* Dynamic Category Pill Filters */}
        <div className="flex flex-wrap justify-center items-center gap-2 mb-12 border-b border-gray-800/60 pb-6">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                setExpandedPost(null);
              }}
              className={`px-5 py-2 rounded-xl text-xs sm:text-sm font-bold tracking-wide transition-all duration-200 border ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white border-transparent shadow-lg shadow-green-900/20'
                  : 'bg-gray-900/60 text-gray-400 border-gray-800 hover:text-white hover:border-gray-700'
              }`}
            >
              {category} Modules
            </button>
          ))}
        </div>

        {/* Animated Grid Container */}
        <motion.div 
          layout 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredPosts.map((post) => (
              <motion.article
                layout
                key={post.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="bg-gray-900/50 border border-gray-800/80 rounded-2xl overflow-hidden flex flex-col group hover:border-gray-700/60 hover:bg-gray-900 transition-all duration-300"
              >
                {/* Visual Image Banner Container */}
                <div className="relative h-48 w-full overflow-hidden bg-gray-950 border-b border-gray-800/40">
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500 opacity-90 group-hover:opacity-100" 
                    loading="lazy" 
                  />
                  <div className="absolute top-3 left-3 bg-gray-950/80 backdrop-blur-md text-gray-300 text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-md border border-gray-800/60 flex items-center gap-1.5">
                    <BookOpen className="w-3 h-3 text-green-500" /> {post.category}
                  </div>
                  <div className="absolute top-3 right-3 bg-gray-950/80 backdrop-blur-md text-gray-400 text-[10px] font-medium px-2.5 py-1 rounded-md border border-gray-800/60 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {post.readTime}
                  </div>
                </div>

                {/* Card Body Information */}
                <div className="p-5 sm:p-6 flex-grow flex flex-col justify-between">
                  <div>
                    <h2 className="text-xl font-bold tracking-tight text-white mb-3 group-hover:text-green-400 transition-colors duration-200 line-clamp-2">
                      {post.title}
                    </h2>
                    
                    <div className="text-xs sm:text-sm text-gray-400 font-medium leading-relaxed">
                      {expandedPost === post.id ? (
                        <motion.p 
                          initial={{ opacity: 0 }} 
                          animate={{ opacity: 1 }}
                          className="text-gray-300 bg-gray-950/40 p-3 rounded-xl border border-gray-800/40 mt-1"
                        >
                          {post.content}
                        </motion.p>
                      ) : (
                        <p className="line-clamp-3">{post.content}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-gray-800/40 flex items-center justify-between">
                    <button
                      onClick={() => setExpandedPost(expandedPost === post.id ? null : post.id)}
                      className="text-xs font-bold uppercase tracking-wider text-green-500 hover:text-green-400 flex items-center gap-1.5 group/btn transition-colors"
                    >
                      {expandedPost === post.id ? 'Collapse View' : 'Read Full Log'}
                      <ArrowRight className={`w-3.5 h-3.5 transform transition-transform duration-200 ${expandedPost === post.id ? 'rotate-90 text-green-400' : 'group-hover/btn:translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Floating Action Button: Back to Top */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-green-500 to-emerald-600 text-white p-3.5 rounded-xl shadow-xl hover:brightness-110 transition-all border border-green-400/20"
            aria-label="Scroll back to top"
          >
            <ArrowUp className="w-5 h-5 stroke-[2.5]" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BlogPosts;